import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../core'
import {SalePaymentStatus} from '../../../helpers'

import * as Yup from 'yup'
import {useFormik} from 'formik'

import {
  KTCard,
  KTCardBody,
  Select,
  ChartColumn,
  ChartDonut,
  Loading,
  Datepicker,
} from '../../../components'

import {useDispatch, useSelector} from 'react-redux'
import {categoryGetAll} from '../../../redux/slices/category.slice'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {
  saleInvoiceChartCategoryGetAll,
  saleInvoiceChartProjectGetAll,
  saleInvoiceChartDateGetAll,
  saleInvoiceChartMonthGetAll,
  saleInvoiceChartStatusGetAll,
  saleInvoiceRealEstateChartMonthGetAll,
} from '../../../redux/slices/reporting.slice'

import {
  useMemoizedValidationSchema,
  useMemoizedFormValues
} from '../../../utils/performanceOptimizations'

const selectCategoryData = (state) => ({
  category: state.category.category
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList
})

const selectReportingData = (state) => ({
  loading: state.reporting.loading,
  saleinvoiceCategory: state.reporting.saleinvoiceCategory,
  saleinvoiceProject: state.reporting.saleinvoiceProject,
  saleinvoiceDate: state.reporting.saleinvoiceDate,
  saleinvoiceMonth: state.reporting.saleinvoiceMonth,
  saleinvoiceStatus: state.reporting.saleinvoiceStatus,
  saleinvoiceRealEstateMonth: state.reporting.saleinvoiceRealEstateMonth,
})

function Report() {
  const [categoryCategories, setCategoryCategories] = useState([])
  const [categoryData, setCategoryData] = useState([])
  const [projectProjects, setProjectProjects] = useState([])
  const [projectData, setProjectData] = useState([])
  const [monthCategories, setMonthCategories] = useState([])
  const [monthData, setMonthData] = useState([])
  const [statusCategories, setStatusCategories] = useState([])
  const [statusData, setStatusData] = useState([])
  const [dateCategories, setDateCategories] = useState([])
  const [dateData, setDateData] = useState([])
  const [dateParent, setDateParent] = useState([])
  const [realEstateMonthCategories, setRealEstateMonthCategories] = useState([])
  const [realEstateMonthData, setRealEstateMonthData] = useState([])

  const {category} = useSelector(selectCategoryData)
  const {projectLookupList} = useSelector(selectLookupData)
  const {
    loading,
    saleinvoiceCategory,
    saleinvoiceProject,
    saleinvoiceDate,
    saleinvoiceMonth,
    saleinvoiceStatus,
    saleinvoiceRealEstateMonth,
  } = useSelector(selectReportingData)

  const intl = useIntl()
  const dispatch = useDispatch()

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({}), [])

  const firsDateMonth = useMemo(() => new Date().setDate(1), [])

  const formValues = useMemoizedFormValues({
    SaleCategoryId: '',
    PaymentStatusId: '',
    ProjectId: '',
    StartDate: new Date(firsDateMonth),
    EndDate: new Date(),
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(saleInvoiceChartCategoryGetAll(values))
    dispatch(saleInvoiceChartProjectGetAll(values))
    dispatch(saleInvoiceChartDateGetAll(values))
    dispatch(saleInvoiceChartMonthGetAll(values))
    dispatch(saleInvoiceChartStatusGetAll(values))
    dispatch(saleInvoiceRealEstateChartMonthGetAll(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    dispatch(categoryGetAll({SourceTypeId: 7}))
    dispatch(projectLookup())
  }, [dispatch])

  const formatCurrency = useCallback((val) => {
    return intl.formatNumber(val, {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4})
  }, [intl])

  const chartTooltip = useMemo(() => ({
    y: {
      formatter: formatCurrency,
    },
  }), [formatCurrency])

  const dateChartTooltip = useMemo(() => ({
    y: {
      formatter: formatCurrency,
      title: {
        formatter: (a, data) => {
          return dateParent[data.dataPointIndex]
        },
      },
    },
  }), [formatCurrency, dateParent])

  useEffect(() => {
    if (saleinvoiceCategory) {
      const saleinvoiceCategoryCategories = []
      const saleinvoiceCategoryData = []

      saleinvoiceCategory && saleinvoiceCategory.map((item) => {
        saleinvoiceCategoryCategories.push(item.title)
        saleinvoiceCategoryData.push(item.dataCount)
      })

      setCategoryCategories(saleinvoiceCategoryCategories)
      setCategoryData(saleinvoiceCategoryData)
    }

    if (saleinvoiceProject) {
      const saleinvoiceProjectProjects = []
      const saleinvoiceProjectData = []

      saleinvoiceProject && saleinvoiceProject.map((item) => {
        saleinvoiceProjectProjects.push(item.title)
        saleinvoiceProjectData.push(item.dataCount)
      })

      setProjectProjects(saleinvoiceProjectProjects)
      setProjectData(saleinvoiceProjectData)
    }

    if (saleinvoiceMonth) {
      const saleinvoiceMonthCategories = []
      const saleinvoiceMonthData = []

      saleinvoiceMonth && saleinvoiceMonth.map((item) => {
        saleinvoiceMonthCategories.push(item.title)
        saleinvoiceMonthData.push(item.dataCount)
      })

      setMonthCategories(saleinvoiceMonthCategories)
      setMonthData(saleinvoiceMonthData)
    }

    if (saleinvoiceStatus) {
      const saleinvoiceStatusCategories = []
      const saleinvoiceStatusData = []

      saleinvoiceStatus && saleinvoiceStatus.map((item) => {
        saleinvoiceStatusCategories.push(item.title)
        saleinvoiceStatusData.push(item.dataCount)
      })

      setStatusCategories(saleinvoiceStatusCategories)
      setStatusData(saleinvoiceStatusData)
    }

    if (saleinvoiceDate) {
      const saleinvoiceDateCategories = []
      const saleinvoiceDateData = []
      const saleinvoiceDateParent = []

      saleinvoiceDate && saleinvoiceDate.map((item) => {
        saleinvoiceDateCategories.push(item.title)
        saleinvoiceDateData.push(item.dataCount)
        saleinvoiceDateParent.push(item.parentName)
      })

      setDateCategories(saleinvoiceDateCategories)
      setDateData(saleinvoiceDateData)
      setDateParent(saleinvoiceDateParent)
    }

    if (saleinvoiceRealEstateMonth) {
      const realEstateCategories = []
      const realEstateData = []

      saleinvoiceRealEstateMonth.map((item) => {
        realEstateCategories.push(item.title)
        realEstateData.push(item.dataCount)
      })

      setRealEstateMonthCategories(realEstateCategories)
      setRealEstateMonthData(realEstateData)
    }
  }, [
    saleinvoiceCategory,
    saleinvoiceProject,
    saleinvoiceDate,
    saleinvoiceMonth,
    saleinvoiceStatus,
    saleinvoiceRealEstateMonth,
  ])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.REPORTING.SALEINVOICE'})}</PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard className='mb-5'>
            <KTCardBody className='p-9'>
              <form className='form' onSubmit={formik.handleSubmit} noValidate>
                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'REPORT.SALEINVOICE.STARTDATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('StartDate')}
                          onChange={([date]) => formik.setFieldValue('StartDate', date)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'REPORT.SALEINVOICE.ENDDATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('EndDate')}
                          onChange={([date]) => formik.setFieldValue('EndDate', date)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.SALEINVOICE.SALECATEGORY'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={category}
                          {...formik.getFieldProps('SaleCategoryId')}
                          onChange={(item) =>
                            formik.setFieldValue('SaleCategoryId', Number(item && item.value))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.SALEINVOICE.PAYMENTSTATUS'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={SalePaymentStatus}
                          {...formik.getFieldProps('PaymentStatusId')}
                          onChange={(item) =>
                            formik.setFieldValue('PaymentStatusId', Number(item && item.value))
                          }
                          textType={'intl'}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.SALEINVOICE.PROJECT'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={projectLookupList}
                          {...formik.getFieldProps('ProjectId')}
                          onChange={(item) =>
                            formik.setFieldValue('ProjectId', Number(item && item.value))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row pt-5'>
                  <div className='col-lg-12 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SEARCH'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      <div className='row gy-5 g-xl-10'>
        {statusData && statusData.length > 0 && (
          <div className='col-xl-6'>
            <ChartDonut
              chartHeight='400px'
              categories={statusCategories}
              data={statusData}
              title={intl.formatMessage({id: 'REPORT.SALEINVOICE.STATUS'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {monthData && monthData.length > 0 && (
          <div className='col-xl-6'>
            <ChartColumn
              chartHeight='400px'
              categories={monthCategories}
              data={monthData}
              title={intl.formatMessage({id: 'REPORT.SALEINVOICE.MONTH'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.SALEREPORT.CATEGORYTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {categoryData && categoryData.length > 0 && (
          <div className='col-xl-6'>
            <ChartColumn
              chartHeight='400px'
              categories={categoryCategories}
              data={categoryData}
              title={intl.formatMessage({id: 'REPORT.SALEINVOICE.CATEGORY'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.SALEREPORT.CATEGORYTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {realEstateMonthData && realEstateMonthData.length > 0 && (
          <div className='col-xl-6'>
            <ChartColumn
              chartHeight='400px'
              categories={realEstateMonthCategories}
              data={realEstateMonthData}
              title={intl.formatMessage({id: 'REPORT.SALEINVOICE.REALESTATE.MONTH'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.SALEREPORT.CATEGORYTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {projectData && projectData.length > 0 && (
          <div className='col-xl-12'>
            <ChartColumn
              chartHeight='400px'
              categories={projectProjects}
              data={projectData}
              title={intl.formatMessage({id: 'REPORT.SALEINVOICE.PROJECT'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.SALEREPORT.PROJECTTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {dateData && dateData.length > 0 && (
          <div className='col-xl-12'>
            <ChartColumn
              chartHeight='400px'
              categories={dateCategories}
              data={dateData}
              title={intl.formatMessage({id: 'REPORT.SALEINVOICE.DATE'})}
              tooltip={dateChartTooltip}
            />
          </div>
        )}
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Report}
